import React, { useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import * as yup from 'yup';
import { useMutation } from 'react-query';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import useNotification from '../../../utils/hooks/useNotification';

// Services
import useEstablishmentService from '../../../services/establishmentService';

// Components
import InputField from '../../../components/atoms/InputField/InputField';
import Button from '../../../components/atoms/Button/Button';
import useModal from '../../../utils/hooks/useModal';
import FlashMessage from '../../../components/molecules/FlashMessage/FlashMessage';

function CreateEstablishmentModal({ refetchEstablishments }) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { postEstablishment } = useEstablishmentService();
  const { displayNotification } = useNotification();
  const { closeModal } = useModal();

  const [postError, setPostError] = useState(null);

  const { mutate: postEstablishmentMutate, isLoading } = useMutation({
    mutationFn: postEstablishment,
    onSuccess: (_, values) => {
      closeModal();
      refetchEstablishments();

      displayNotification({
        message: t('establishments.table.add.success', { name: values.name }),
      });
    },
    onError: (error) => {
      setPostError(
        t(error?.response?.data?.code
          ? `code_errors.${error.response.data.code}`
          : 'common.saveError'),
      );
    },
  });

  const formik = useFormik({
    initialValues: {
      name: '',
    },
    validationSchema: yup.object().shape({
      name: yup.string().required('common.required'),
    }),
    onSubmit: (values) => {
      setPostError(null);
      postEstablishmentMutate({ name: values.name });
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      {postError ? (
        <div className="d-flex m-b-20">
          <FlashMessage message={postError} color="error" />
        </div>
      ) : null}

      <InputField
        id="name"
        name="name"
        label={t('establishments.table.add.name')}
        value={formik.values?.name || ''}
        full
        error={formik?.errors?.name && formik?.touched?.name ? formik.errors.name : null}
        isRequired
        formik={{
          handleChange: formik.handleChange,
          handleBlur: formik.handleBlur,
        }}
      />

      <div className="d-flex a-center j-end m-t-30 mobile-d-col">
        <Button
          theme="inverted"
          className="m-r-10 mobile-m-r-0 mobile-m-b-10"
          disabled={isLoading}
          onClick={closeModal}
          full={isMobile}
        >
          {t('common.cancel')}
        </Button>

        <Button
          submitType
          isLoading={isLoading}
          disabled={!formik.dirty}
          full={isMobile}
        >
          {t('establishments.table.add.submit')}
        </Button>
      </div>
    </form>
  );
}

CreateEstablishmentModal.propTypes = {
  refetchEstablishments: PropTypes.func,
};

CreateEstablishmentModal.defaultProps = {
  refetchEstablishments: () => {},
};

export default CreateEstablishmentModal;
